"use client";

import React from "react";
import {
  Phone,
  Mail,
  MoreHorizontal,
  MapPin,
  Calendar,
  ShoppingBag,
  DollarSign,
} from "lucide-react";
import { ButtonIcon } from "@/components/common/StandardButton";
import StandardCard from "@/components/common/StandardCard";
import { Customer } from "@/types/models/customer";
import { formatCurrency } from "@/utils/currency";
import { format } from "date-fns";
import { Avatar } from "@/components/ui/avatar";

interface CustomerInformationCardProps {
  customer: Customer | null;
  loading?: boolean;
  error?: string | null;
}

const CustomerInformationCard: React.FC<CustomerInformationCardProps> = ({
  customer,
  loading = false,
  error = null,
}) => {
  if (loading) {
    return (
      <StandardCard>
        <div className="space-y-4 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-muted rounded-full"></div>
            <div className="flex-1">
              <div className="h-4 bg-muted rounded w-32 mb-2"></div>
              <div className="h-3 bg-muted rounded w-48"></div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="h-4 bg-muted rounded w-24 mb-2"></div>
            <div className="h-3 bg-muted rounded w-36"></div>
            <div className="h-3 bg-muted rounded w-40"></div>
            <div className="h-3 bg-muted rounded w-44"></div>
          </div>
        </div>
      </StandardCard>
    );
  }

  if (error || !customer) {
    return (
      <StandardCard>
        <div className="text-center text-muted-foreground">
          {error || "No customer information available"}
        </div>
      </StandardCard>
    );
  }

  const customerName =
    `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || "N/A";

  const defaultAddress = customer?.customer_addresses?.find(
    (addr) => addr.id === customer.default_address_id,
  );
  const billingAddress = customer?.customer_addresses?.find(
    (addr) => addr.billing_address,
  );

  return (
    <StandardCard>
      <div className="space-y-4">
        {/* Customer Profile */}
        <div className="flex items-center gap-3">
          <Avatar name={customerName} className="h-12 w-12" />
          <div className="flex-1">
            <h3 className="font-medium text-foreground">{customerName}</h3>
            <p className="text-sm text-muted-foreground">
              {customer.email || "No email"}
            </p>
          </div>
          <ButtonIcon variant="ghost">
            <MoreHorizontal className="h-4 w-4" />
          </ButtonIcon>
        </div>

        {/* Customer Information Section */}
        <div>
          <h4 className="text-sm font-medium text-primary mb-3 uppercase tracking-wider">
            Customer Information
          </h4>
          <div className="space-y-3">
            {customer.phone_number && (
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-foreground">
                  {customer.phone_number}
                </span>
              </div>
            )}
            {customer.email && (
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-foreground">
                  {customer.email}
                </span>
              </div>
            )}
            {customer.total_orders !== undefined && (
              <div className="flex items-center gap-3">
                <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Total Orders:
                </span>
                <span className="text-sm text-foreground">
                  {customer.total_orders}
                </span>
              </div>
            )}
            {customer.total_spent !== undefined && (
              <div className="flex items-center gap-3">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Total Spent:
                </span>
                <span className="text-sm text-foreground">
                  {formatCurrency(customer.total_spent || 0)}
                </span>
              </div>
            )}
            {customer.last_order_date && (
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Last Order:
                </span>
                <span className="text-sm text-foreground">
                  {format(new Date(customer.last_order_date), "MMM dd, yyyy")}
                </span>
              </div>
            )}
            {customer.last_active_date && (
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Last Active:
                </span>
                <span className="text-sm text-foreground">
                  {format(new Date(customer.last_active_date), "MMM dd, yyyy")}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Default Address */}
        {defaultAddress && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-primary uppercase tracking-wider">
                Default Address
              </h4>
              <ButtonIcon variant="ghost">
                <MoreHorizontal className="h-4 w-4" />
              </ButtonIcon>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              <p className="text-sm text-foreground leading-relaxed">
                {defaultAddress.address_line_one}
                {defaultAddress.address_line_two &&
                  `, ${defaultAddress.address_line_two}`}
                <br />
                {defaultAddress.city}, {defaultAddress.state} -{" "}
                {defaultAddress.postal_code}
              </p>
            </div>
          </div>
        )}

        {/* Billing Address */}
        {billingAddress && billingAddress.id !== defaultAddress?.id && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-primary uppercase tracking-wider">
                Billing Address
              </h4>
              <ButtonIcon variant="ghost">
                <MoreHorizontal className="h-4 w-4" />
              </ButtonIcon>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              <p className="text-sm text-foreground leading-relaxed">
                {billingAddress.address_line_one}
                {billingAddress.address_line_two &&
                  `, ${billingAddress.address_line_two}`}
                <br />
                {billingAddress.city}, {billingAddress.state} -{" "}
                {billingAddress.postal_code}
              </p>
            </div>
          </div>
        )}

        {/* All Addresses Count */}
        {customer.customer_addresses &&
          customer.customer_addresses.length > 1 && (
            <div className="pt-2 border-t border-border">
              <span className="text-xs text-muted-foreground">
                {customer.customer_addresses.length} address
                {customer.customer_addresses.length !== 1 ? "es" : ""} total
              </span>
            </div>
          )}
      </div>
    </StandardCard>
  );
};

export default CustomerInformationCard;
